import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, X, ExternalLink, CheckCheck, Sparkles, Volume2, Info, ChevronRight, ShieldCheck, Tag, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { User } from '../types';
import { API_URL } from '../constants';

export interface PwaNotificationItem {
  id: string;
  title: string;
  message: string;
  body?: string;
  targetType?: string;
  targetUserId?: string;
  targetUserName?: string;
  destinationName?: string;
  link?: string;
  imageUrl?: string;
  image?: string;
  imageWidth?: number;
  imageHeight?: number;
  imageSize?: string;
  sentAt: string;
  read?: boolean;
}

interface NotificationModalProps {
  currentUser?: User | null;
  apiUrl?: string;
  isOpenMural: boolean;
  onCloseMural: () => void;
  onNavigate?: (page: string) => void;
  onUnreadCountChange?: (count: number) => void;
}

export default function NotificationModal({
  currentUser,
  apiUrl = API_URL,
  isOpenMural,
  onCloseMural,
  onNavigate,
  onUnreadCountChange
}: NotificationModalProps) {
  // Local storage for read notifications
  const [seenIds, setSeenIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('seen_pwa_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Local storage for dismissed/deleted notifications (logged in users)
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('dismissed_pwa_notifications');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  // Popup & History state
  const [latestPopup, setLatestPopup] = useState<PwaNotificationItem | null>(null);
  const [showPopupModal, setShowPopupModal] = useState<boolean>(false);
  const [notificationsList, setNotificationsList] = useState<PwaNotificationItem[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');

  // Audio effect reference
  const audioContextRef = useRef<AudioContext | null>(null);

  const playChimeSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioCtx();
      }
      const ctx = audioContextRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();

      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
      osc1.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15); // A5

      gain1.gain.setValueAtTime(0.2, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);

      osc1.connect(gain1);
      gain1.connect(ctx.destination);

      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.4);
    } catch (e) {
      console.warn('Audio chime fallback disabled:', e);
    }
  }, []);

  const markAsSeen = useCallback((notifId: string) => {
    setSeenIds(prev => {
      if (prev.includes(notifId)) return prev;
      const updated = [...prev, notifId];
      try {
        localStorage.setItem('seen_pwa_notifications', JSON.stringify(updated));
      } catch (e) {}
      return updated;
    });

    // Also mark in database if user inbox exists
    if (currentUser?.id) {
      fetch(`${apiUrl}?action=mark_as_read_user_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: notifId, userId: currentUser.id.toString() })
      }).catch(() => {});
    }
  }, [currentUser?.id, apiUrl]);

  const markAllAsSeen = () => {
    const allIds = notificationsList.map(n => n.id);
    setSeenIds(allIds);
    try {
      localStorage.setItem('seen_pwa_notifications', JSON.stringify(allIds));
    } catch (e) {}

    if (currentUser?.id) {
      fetch(`${apiUrl}?action=mark_as_read_user_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: 'all', userId: currentUser.id.toString() })
      }).catch(() => {});
    }
  };

  const handleDeleteItem = (e: React.MouseEvent, notifId: string) => {
    e.stopPropagation();
    if (!currentUser) return; // Only logged in users can delete

    setDismissedIds(prev => {
      const updated = [...prev, notifId];
      try {
        localStorage.setItem('dismissed_pwa_notifications', JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });

    setNotificationsList(prev => prev.filter(item => item.id !== notifId));

    if (currentUser?.id) {
      fetch(`${apiUrl}?action=delete_user_notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: notifId, userId: currentUser.id.toString() })
      }).catch(() => {});
    }
  };

  const handleClearAll = () => {
    if (!currentUser) return; // Only logged in users can clear

    if (!window.confirm("Deseja realmente apagar todas as notificações do seu mural?")) return;

    const allIds = notificationsList.map(n => n.id);
    setDismissedIds(prev => {
      const updated = Array.from(new Set([...prev, ...allIds]));
      try {
        localStorage.setItem('dismissed_pwa_notifications', JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });

    setNotificationsList([]);

    if (currentUser?.id) {
      fetch(`${apiUrl}?action=clear_all_user_notifications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUser.id.toString() })
      }).catch(() => {});
    }
  };

  // Ref for callback and previous unread count to avoid re-render loops
  const onUnreadCountChangeRef = useRef(onUnreadCountChange);
  useEffect(() => {
    onUnreadCountChangeRef.current = onUnreadCountChange;
  }, [onUnreadCountChange]);

  const lastUnreadCountRef = useRef<number | null>(null);

  // Poll for latest notification
  const checkNotifications = useCallback(async () => {
    try {
      // 1. Get latest campaign broadcast
      const res = await fetch(`${apiUrl}?action=get_latest_notification`);
      if (!res.ok) return;
      const latest: PwaNotificationItem | null = await res.json();

      if (latest && latest.id) {
        const tType = latest.targetType || 'all';
        const isTargetedToMe = 
          tType === 'all' || 
          tType === 'panel_all' ||
          (tType === 'logged_in' && !!currentUser) ||
          (tType === 'logged_out' && !currentUser) ||
          (currentUser && latest.targetUserId && latest.targetUserId.split(',').map(s => s.trim()).includes(currentUser.id.toString()));

        const alreadySeen = seenIds.includes(latest.id);

        if (isTargetedToMe && !alreadySeen) {
          setLatestPopup(latest);
          setShowPopupModal(true);
          playChimeSound();
        }
      }

      // 2. Get list for Mural de Notificações
      let muralItems: PwaNotificationItem[] = [];

      // Fetch general sent notifications history
      const sentRes = await fetch(`${apiUrl}?action=list_sent_notifications`);
      let allSent: PwaNotificationItem[] = [];
      if (sentRes.ok) {
        const rawSent = await sentRes.json();
        if (Array.isArray(rawSent)) {
          allSent = rawSent.map((item: any) => ({
            id: item.id,
            title: item.title,
            message: item.message || item.body,
            targetType: item.target_type || item.targetType,
            targetUserId: item.target_user_id || item.targetUserId,
            destinationName: item.destination_name || item.destinationName,
            link: item.link || item.destination_name || item.destinationName,
            imageUrl: item.image_url || item.imageUrl,
            imageWidth: item.image_width || item.imageWidth,
            imageHeight: item.image_height || item.imageHeight,
            imageSize: item.image_size || item.imageSize,
            sentAt: item.sent_at || item.sentAt,
            read: false,
          }));
        }
      }

      if (currentUser?.id) {
        // User is LOGGED IN: Get personal inbox + relevant public broadcasts
        const userInboxRes = await fetch(`${apiUrl}?action=list_user_notifications&user_id=${encodeURIComponent(currentUser.id.toString())}`);
        let userInbox: PwaNotificationItem[] = [];
        if (userInboxRes.ok) {
          const rawInbox = await userInboxRes.json();
          if (Array.isArray(rawInbox)) {
            userInbox = rawInbox;
          }
        }

        // Filter broadcasts relevant for logged in users
        const relevantBroadcasts = allSent.filter(item => {
          const t = item.targetType || 'all';
          return t === 'all' || 
                 t === 'logged_in' || 
                 t === 'panel_all' ||
                 (item.targetUserId && item.targetUserId.split(',').map(s => s.trim()).includes(currentUser.id.toString()));
        });

        // Merge inbox and broadcasts, removing duplicates
        const map = new Map<string, PwaNotificationItem>();
        userInbox.forEach(i => map.set(i.id, i));
        relevantBroadcasts.forEach(b => {
          if (!map.has(b.id)) map.set(b.id, b);
        });

        muralItems = Array.from(map.values())
          .filter(item => !dismissedIds.includes(item.id))
          .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
          .slice(0, 50);
      } else {
        // User is LOGGED OUT / VISITOR: Filter broadcasts for "all" or "logged_out"
        // STRICT LIMIT OF 10 MESSAGES FOR VISITORS! Older ones roll off automatically.
        muralItems = allSent
          .filter(item => {
            const t = item.targetType || 'all';
            return t === 'all' || t === 'logged_out';
          })
          .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime())
          .slice(0, 10);
      }

      // Fallback if list is empty
      if (muralItems.length === 0 && latest && (!currentUser || !dismissedIds.includes(latest.id))) {
        muralItems = [latest];
      }

      setNotificationsList(muralItems);

      // Calculate unread count and notify parent ONLY if changed
      const unreadCount = muralItems.filter(item => !seenIds.includes(item.id) && !item.read).length;
      if (unreadCount !== lastUnreadCountRef.current) {
        lastUnreadCountRef.current = unreadCount;
        if (onUnreadCountChangeRef.current) {
          onUnreadCountChangeRef.current(unreadCount);
        }
      }

    } catch (e) {
      console.warn('Notification check poll error:', e);
    }
  }, [apiUrl, currentUser, seenIds, dismissedIds, playChimeSound]);

  useEffect(() => {
    checkNotifications();
    const interval = setInterval(checkNotifications, 30000);
    return () => clearInterval(interval);
  }, [checkNotifications]);

  const handleActionClick = (link?: string) => {
    if (latestPopup) {
      markAsSeen(latestPopup.id);
    }
    setShowPopupModal(false);

    if (!link || link === '/') return;

    if (link.startsWith('http://') || link.startsWith('https://')) {
      window.open(link, '_blank');
    } else if (onNavigate) {
      onNavigate(link.replace('/', ''));
    } else {
      window.location.href = link;
    }
  };

  const unreadItems = notificationsList.filter(n => !seenIds.includes(n.id) && !n.read);

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* 1. POP-UP DESTAQUE EM TELA (Real-Time Notification Center Modal)  */}
      {/* ------------------------------------------------------------------ */}
      <AnimatePresence>
        {showPopupModal && latestPopup && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md select-none">
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.85, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="relative bg-[#0b0922] border border-indigo-500/30 rounded-3xl shadow-[0_25px_70px_rgba(79,70,229,0.35)] w-full max-w-md overflow-hidden text-white flex flex-col"
            >
              {/* Header Gradient Line */}
              <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />

              {/* Close Button */}
              <button
                onClick={() => {
                  if (latestPopup) markAsSeen(latestPopup.id);
                  setShowPopupModal(false);
                }}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer z-20"
              >
                <X size={18} />
              </button>

              <div className="p-6 md:p-8 flex flex-col items-center text-center space-y-5">
                {/* Bell Icon Pulsating */}
                <div className="relative mt-2">
                  <div className="absolute inset-0 rounded-2xl bg-indigo-500/30 blur-xl animate-pulse" />
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-700 flex items-center justify-center text-amber-300 border border-indigo-400/30 shadow-xl">
                    <Bell size={32} className="animate-bounce" />
                  </div>
                  <div className="absolute -top-1 -right-1 bg-pink-500 text-white text-[9px] font-black uppercase px-2 py-0.5 rounded-full border border-[#0b0922] shadow">
                    NOVO AVISO
                  </div>
                </div>

                {/* Banner / Image preview if present */}
                {(latestPopup.imageUrl || latestPopup.image) && (
                  <div className="relative w-full rounded-2xl overflow-hidden border border-white/10 group shadow-lg">
                    <img
                      src={latestPopup.imageUrl || latestPopup.image}
                      alt={latestPopup.title}
                      className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {(latestPopup.imageWidth || latestPopup.imageHeight) && (
                      <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-md text-[10px] font-bold text-slate-300 px-2 py-1 rounded-md border border-white/10">
                        {latestPopup.imageWidth || '800'}x{latestPopup.imageHeight || '400'} px
                      </div>
                    )}
                  </div>
                )}

                {/* Title & Body */}
                <div className="space-y-2">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-indigo-400 bg-indigo-500/10 px-3 py-1 rounded-full border border-indigo-500/20">
                    Notificação do Sistema PWA
                  </span>
                  <h3 className="text-xl md:text-2xl font-black text-white leading-snug">
                    {latestPopup.title}
                  </h3>
                  <p className="text-xs md:text-sm text-slate-300 leading-relaxed font-medium">
                    {latestPopup.message || latestPopup.body}
                  </p>
                </div>

                {/* Buttons Action */}
                <div className="w-full pt-2 flex flex-col sm:flex-row items-center gap-2.5">
                  <button
                    onClick={() => {
                      markAsSeen(latestPopup.id);
                      setShowPopupModal(false);
                    }}
                    className="w-full sm:w-1/2 py-3 px-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white font-bold text-xs uppercase tracking-wider transition-all cursor-pointer"
                  >
                    Entendi, Fechar
                  </button>

                  <button
                    onClick={() => handleActionClick(latestPopup.link || latestPopup.destinationName)}
                    className="w-full sm:w-1/2 py-3 px-4 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-black text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg shadow-indigo-500/25 active:scale-95 flex items-center justify-center gap-1.5"
                  >
                    Ir ao Destino <ExternalLink size={14} />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ------------------------------------------------------------------ */}
      {/* 2. MURAL DE NOTIFICAÇÕES (Notification Feed / Wall Drawer)        */}
      {/* ------------------------------------------------------------------ */}
      <AnimatePresence>
        {isOpenMural && (
          <div className="fixed inset-0 z-[9999] flex justify-end bg-black/75 backdrop-blur-sm select-none">
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="relative w-full max-w-md h-full bg-[#0b0922] border-l border-white/10 text-white flex flex-col shadow-2xl"
            >
              {/* Header */}
              <div className="p-5 border-b border-white/10 bg-gradient-to-r from-indigo-950/40 via-purple-950/30 to-slate-900/50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-600/20 border border-indigo-500/30 flex items-center justify-center text-amber-300">
                    <Bell size={20} />
                  </div>
                  <div>
                    <h2 className="text-base font-black text-white">Mural de Notificações</h2>
                    <p className="text-[11px] text-slate-400 font-medium">Fique por dentro de avisos e novidades</p>
                  </div>
                </div>

                <button
                  onClick={onCloseMural}
                  className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Quick Actions Bar */}
              <div className="p-4 bg-black/30 border-b border-white/5 flex items-center justify-between gap-2">
                <div className="flex gap-1 bg-white/5 p-1 rounded-xl border border-white/5">
                  <button
                    onClick={() => setActiveTab('all')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activeTab === 'all' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Todas ({notificationsList.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('unread')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activeTab === 'unread' ? 'bg-indigo-600 text-white shadow' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Não lidas ({unreadItems.length})
                  </button>
                </div>

                <div className="flex items-center gap-1.5">
                  {unreadItems.length > 0 && (
                    <button
                      onClick={markAllAsSeen}
                      className="text-[11px] font-bold text-indigo-400 hover:text-indigo-300 flex items-center gap-1 cursor-pointer bg-indigo-500/10 px-2.5 py-1.5 rounded-lg border border-indigo-500/20"
                    >
                      <CheckCheck size={14} /> Ler Tudo
                    </button>
                  )}

                  {currentUser && notificationsList.length > 0 && (
                    <button
                      onClick={handleClearAll}
                      title="Apagar todas as notificações do seu mural"
                      className="text-[11px] font-bold text-rose-400 hover:text-rose-300 flex items-center gap-1 cursor-pointer bg-rose-500/10 px-2.5 py-1.5 rounded-lg border border-rose-500/20 transition-all"
                    >
                      <Trash2 size={13} /> Limpar
                    </button>
                  )}
                </div>
              </div>

              {/* Notification Items List */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {(activeTab === 'all' ? notificationsList : unreadItems).length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-64 text-center p-6 space-y-3">
                    <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-slate-500">
                      <Bell size={28} />
                    </div>
                    <p className="text-sm font-bold text-slate-300">Nenhuma notificação por aqui!</p>
                    <p className="text-xs text-slate-500 max-w-xs">
                      Assim que novas ofertas ou avisos forem enviados, eles aparecerão neste mural.
                    </p>
                  </div>
                ) : (
                  (activeTab === 'all' ? notificationsList : unreadItems).map(item => {
                    const isUnread = !seenIds.includes(item.id) && !item.read;
                    return (
                      <div
                        key={item.id}
                        onClick={() => markAsSeen(item.id)}
                        className={`p-4 rounded-2xl border transition-all cursor-pointer relative group ${
                          isUnread
                            ? 'bg-gradient-to-r from-indigo-950/50 via-purple-950/30 to-slate-900/60 border-indigo-500/40 shadow-lg'
                            : 'bg-white/5 border-white/5 opacity-80 hover:opacity-100 hover:border-white/20'
                        }`}
                      >
                        {/* Top right actions: unread ping + delete button for logged-in users */}
                        <div className="absolute top-3.5 right-3.5 flex items-center gap-2 z-10">
                          {isUnread && (
                            <div className="w-2.5 h-2.5 rounded-full bg-amber-400 animate-ping" />
                          )}
                          {currentUser && (
                            <button
                              type="button"
                              onClick={(e) => handleDeleteItem(e, item.id)}
                              title="Excluir notificação"
                              className="opacity-60 hover:opacity-100 p-1 rounded-lg hover:bg-rose-500/20 text-slate-400 hover:text-rose-400 transition-all cursor-pointer"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>

                        <div className="flex items-start gap-3">
                          <div className={`p-2.5 rounded-xl border shrink-0 mt-0.5 ${
                            isUnread ? 'bg-indigo-600/20 border-indigo-500/40 text-indigo-300' : 'bg-white/5 border-white/10 text-slate-400'
                          }`}>
                            <Sparkles size={16} />
                          </div>

                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-black uppercase tracking-wider text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded-md border border-indigo-500/20">
                                {item.targetType === 'single_user' ? 'Exclusivo' : 'Geral'}
                              </span>
                              <span className="text-[10px] font-medium text-slate-400">
                                {new Date(item.sentAt).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>

                            <h4 className="text-sm font-bold text-white group-hover:text-indigo-300 transition-colors pr-6">
                              {item.title}
                            </h4>

                            <p className="text-xs text-slate-300 leading-relaxed line-clamp-3 font-medium">
                              {item.message || item.body}
                            </p>

                            {(item.imageUrl || item.image) && (
                              <div className="mt-2 rounded-xl overflow-hidden border border-white/10">
                                <img
                                  src={item.imageUrl || item.image}
                                  alt={item.title}
                                  className="w-full h-32 object-cover"
                                />
                              </div>
                            )}

                            {(item.link || item.destinationName) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActionClick(item.link || item.destinationName);
                                }}
                                className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-300 hover:text-amber-200 transition-all"
                              >
                                Ir ao link informado <ChevronRight size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer info */}
              <div className="p-4 border-t border-white/10 bg-black/40 text-center text-[11px] text-slate-400 font-medium">
                {!currentUser ? (
                  <span>
                    📌 <strong>Mural Público:</strong> Exibindo os 10 avisos mais recentes do sistema.
                  </span>
                ) : (
                  <span>
                    🔔 <strong>Mural Pessoal:</strong> {notificationsList.length} notificação(ões) no seu histórico.
                  </span>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
